import { Injectable } from '@angular/core';
import { interval, Observable, of, Subject } from 'rxjs';
import { catchError, map, mapTo, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { PiccDetectEnum } from '../models/picc-detect-enum';
import { SmartCardStatus } from '../models/smart-card-status';
import { StatusConnectionEnum } from '../models/status-connection-enum';
import { SmartCardConnectionHttpService } from './smart-card-connection-http.service';

@Injectable()
export class SmartCardCommunicationService {

  ledEnabled = new Subject<boolean>();

  constructor(
    private smartCardConnectionHttpService: SmartCardConnectionHttpService,
  ) { }

  get enabled(): boolean {
    return this.smartCardConnectionHttpService.isConfigured;
  }

  readCard(retryCount: number = 3): Observable<string> {
    return this.readOneCard().pipe(
      switchMap(cardUid => {
        if (cardUid) {
          return of(cardUid);
        }
        if (retryCount <= 0) {
          throw new Error('Card dispenser error. Card was not produced.');
        }
        return this.readCard(retryCount - 1);
      })
    );
  }

  readOneCard(): Observable<string> {
    return this.ensureThatCardDispenserIsAvailable()
      .pipe(
        switchMap(_ => this.getStatus()),
        switchMap(status => {
          if (status.isCardInserted && status.piccDetectState === PiccDetectEnum.Detected && status.cardUID) {
            return of(status.cardUID);
          }
          if (!status.isCardInserted) {
            return this.produceCard().pipe(
              switchMap(_ => this.controlShutter(false)),
              switchMap(_ => this.piccDetect2()),
            );
          }
          return this.piccDetect2();
        }),
      );
  }

  piccDetect2(): Observable<string> {
    return this.piccDetect().pipe(
      catchError(error => {
        return this.piccDetect().pipe(
          catchError(error2 => {
            return this.captureCard().pipe(
              mapTo(''),
              catchError(error3 => {
                return of('');
              }),
            );
          }),
        );
      }),
    );
  }

  getStatus(): Observable<SmartCardStatus> {
    return this.smartCardConnectionHttpService.getSmartCardDeviceStatus();
  }

  resetCardDispenserState(): Observable<boolean> {
    return this.getStatus()
      .pipe(
        switchMap((status: SmartCardStatus) => {
          if (!status) {
            throw new Error('Card dispenser is not available');
          }

          if (!status.isCardInserted) {
            return this.captureCard();
          }

          return of(true);
        })
      );
  }

  ensureThatCardDispenserIsAvailable(): Observable<boolean> {
    return this.getStatus()
      .pipe(
        map((status: SmartCardStatus) => {
          if (!status) {
            throw new Error('Card dispenser is not available');
          }
          if (status.status !== StatusConnectionEnum.Connected) {
            throw new Error('Card dispenser is not available');
          }

          return true;
        })
      );
  }

  produceCard(): Observable<boolean> {
    return this.smartCardConnectionHttpService.doProduceCardProcedure()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > 10) {
                  throw new Error('Card dispenser error. Card was not produced.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                return status.isCardInserted;
              }),
              skipWhile(done => !done),
              take(1),
            );
        })
      );
  }

  piccDetect(waitTimeSec = 3): Observable<string> {
    return this.smartCardConnectionHttpService.piccDetect()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card was not produced.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                if (status.piccDetectState === PiccDetectEnum.Detected) {
                  return status.cardUID;
                }

                return '';
              }),
              skipWhile(done => !done),
              take(1),
            );
        })
      );
  }

  rereadCardProcedure(waitTimeSec = 10): Observable<string> {
    return this.smartCardConnectionHttpService.rereadCardProcedure()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card was not produced.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                if (status.piccDetectState === PiccDetectEnum.Detected) {
                  return status.cardUID;
                }

                return '';
              }),
              skipWhile(done => !done),
              take(1),
            );
        })
      );
  }

  captureCard(waitTimeSec: number = 10): Observable<any> {
    return this.smartCardConnectionHttpService.mechanicToCaptureCard()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card capture.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }
                return status.isCardDetected;
              }),
              skipWhile(done => done),
              take(1),
            );
        })
      );
  }

  controlShutter(isOpened: boolean): Observable<any> {
    return this.smartCardConnectionHttpService.controlShutter(isOpened);
  }

  canReleaseCard(): Observable<boolean> {
    return this.getStatus()
      .pipe(
        switchMap((status: SmartCardStatus) => {
          if (!status) {
            throw new Error('Card dispenser is not available');
          }
          return of(status.isCardInserted);
        })
      );
  }

  releaseCard(waitCardTakenSec: number = 10, waitTimeSec: number = 10): Observable<any> {
    return this.ensureThatCardDispenserIsAvailable()
      .pipe(
        switchMap(_ => this._releaseCard(waitTimeSec)),
        tap(_ => { this.ledEnabled.next(true); }),
        switchMap(_ => this._waitCardTaken(waitCardTakenSec)),
        tap(_ => { this.ledEnabled.next(false); }),
        switchMap(cardTaken => {
          if (!cardTaken) {
            return this.moveCardToReader(waitTimeSec).pipe(mapTo(false));
          }
          return of(true);
        }),
      );
  }

  moveCardToReader(waitTimeSec: number = 10): Observable<string> {
    return this.intakeCard(waitTimeSec).pipe(
      switchMap(_ => this.moveToContactlessAntenna(waitTimeSec)),
      switchMap(_ => this.controlShutter(false)),
    );
  }

  intakeCard(waitTimeSec: number = 10): Observable<boolean> {
    return this.smartCardConnectionHttpService.intakeCard()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card was not taken.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                return status.isCardInserted;
              }),
              skipWhile(done => !done),
              take(1),
            );
        })
      );
  }

  moveToContactlessAntenna(waitTimeSec: number = 10): Observable<boolean> {
    return this.smartCardConnectionHttpService.moveToContactlessAntenna()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card was not taken.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                return status.isCardNearContactlessReader;
              }),
              skipWhile(done => !done),
              take(1),
            );
        })
      );
  }

  _releaseCard(waitTimeSec: number = 10): Observable<boolean> {
    return this.smartCardConnectionHttpService.doReleaseCardProcedure()
      .pipe(
        switchMap(_ => {
          return interval(1000)
            .pipe(
              tap(attempts => {
                if (attempts > waitTimeSec) {
                  throw new Error('Card dispenser error. Card was not produced.');
                }
              }),
              switchMap(() => this.getStatus()),
              map((status: SmartCardStatus) => {
                if (!status) {
                  throw new Error('Card dispenser is not available');
                }

                return status.isCardInserted;
              }),
              skipWhile(done => done),
              take(1),
            );
        })
      );
  }

  _waitCardTaken(waitTimeSec: number = 10): Observable<boolean> {
    return interval(1000)
      .pipe(
        tap(attempts => {
          if (attempts > waitTimeSec) {
            throw new Error('Card dispenser error. Card was not taken.');
          }
        }),
        switchMap(() => this.getStatus()),
        map((status: SmartCardStatus) => {
          if (!status) {
            throw new Error('Card dispenser is not available');
          }

          return status.isCardReadEmpty;
        }),
        skipWhile(done => !done),
        take(1),
        catchError(error => {
          return of(false);
        })
      );
  }

  takeCard(waitTimeSec: number): Observable<string> {
    return this.getStatus().pipe(
      switchMap((status: SmartCardStatus) => {
        if (status && !status.isCardReadEmpty) {
          return this.captureCard();
        }
        return of(true);
      }),
      switchMap(_ => this.openShutter()),
      tap(_ => { this.ledEnabled.next(true); }),
      switchMap(_ => this.waitCard(waitTimeSec)),
      tap(_ => { this.ledEnabled.next(false); }),
      switchMap(cardExist => {
        if (cardExist) {
          return this.moveCardToReader().pipe(
            switchMap(_ => this.piccDetect(waitTimeSec)),
            switchMap(cardUID => {
              if (!cardUID) {
                return this.piccDetect(waitTimeSec);
              }
              return of(cardUID);
            }),
          );
        }

        return of('');
      }),
    );
  }

  waitCard(waitTimeSec: number): Observable<boolean> {
    return interval(1000)
      .pipe(
        tap(attempts => {
          if (attempts > waitTimeSec) {
            throw new Error('Card dispenser error. Card was not taken.');
          }
        }),
        switchMap(() => this.getStatus()),
        map((status: SmartCardStatus) => {
          if (!status) {
            throw new Error('Card dispenser is not available');
          }

          return !status.isCardReadEmpty;
        }),
        skipWhile(done => !done),
        take(1),
        catchError((error) => of(false))
      );
  }

  openShutter(): Observable<boolean> {
    return this.controlShutter(true);
  }

  closeShutter(): Observable<boolean> {
    return this.controlShutter(false);
  }
}
